import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute } from '@angular/router';
import { AuthEmailActionsService } from '@jfw/angular/ecommerce/core';
import { Site } from '@jfw/types';
import {
  Observable,
  Subject,
  catchError,
  delay,
  filter,
  map,
  of,
  shareReplay,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../services/auth/auth.service';

type EmailVerificationResponse = {
  success: boolean;
  message: string;
};

/// This component is used to re-send email verification to the user.
/// It is where the user is redirected after sign-in if their email is not verified.
@Component({
  selector: 'app-email-not-verified',
  templateUrl: './email-not-verified.component.html',
  styleUrls: ['./email-not-verified.component.scss'],
  imports: [MatButtonModule, MatCardModule, MatProgressSpinnerModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmailNotVerifiedComponent {
  private authService = inject(AuthService);
  private authEmailActionService = inject(AuthEmailActionsService);
  private route = inject(ActivatedRoute);

  /** This is the route that the user will be redirected to after email verification is complete.*/
  private readonly defaultContinueUrlSegment = '/dashboard';

  /** Captures the continueUrl query param from the route.  This is passed along as the continueUrl param in the email verification link.
   * After the user clicks the verification link in the email, and successfully verifies their email, they will be redirected to this url.
   */
  private readonly continueUrl$ = this.route.queryParamMap.pipe(
    map((params) => {
      const segment =
        params.get('continueUrl') ?? this.defaultContinueUrlSegment;
      const continueUrl = `${environment.prefix_for_email_action_links}${segment}`;
      return continueUrl;
    }),
    tap((continueUrl) => console.log('continueUrl$:', continueUrl)),
  );

  public emailVerificationRequest$ = new Subject<void>();

  public sending = signal(false);

  private emailVerificationResponse$: Observable<EmailVerificationResponse> =
    this.emailVerificationRequest$.pipe(
      tap(() => this.sending.set(true)),
      withLatestFrom(this.continueUrl$),
      switchMap(([_, continueUrl]) =>
        this.authEmailActionService
          .sendEmailVerificationApi(Site.DealerPortal, continueUrl)
          .pipe(
            map(() => ({
              success: true,
              message:
                'A verification email has been sent to your email address.  Returning to sign in...',
            })),
            catchError((error) => {
              console.error(error);
              return of({
                success: false,
                message:
                  'Please check your inbox for the verification email. If you do not receive an email, try to resend in a few minutes or contact customer service at (866) 783-5767.',
              });
            }),
          ),
      ),
      tap(() => this.sending.set(false)),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

  public emailVerificationResponse = toSignal(this.emailVerificationResponse$);

  private signOut$ = this.emailVerificationResponse$.pipe(
    filter((response) => response.success),
    delay(3000),
    tap(() => this.authService.signOut()),
  );

  public signOut = toSignal(this.signOut$);
}
